import React, { useMemo, useState } from 'react';
import CurveSpark from './CurveSpark';
import SaveStreak from './SaveStreak';
import { drawRowState } from './curveFormat';
import { useCountUp } from '../../shell/motion';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/* The Curve's right rail — your record and every line you've drawn.
   Newcomers (zero draws) get the only how-it-works on the site; guests get
   the save-your-streak capture; veterans get the accuracy marquee plus a
   searchable wall of their calls. Clicking a row jumps to its full chart. */
const styles = `
  .cvrail { --brand:#FF7A00; --brand-soft:rgba(255,122,0,.13); --brand-ink:#C25E00;
    --ink:#1D1D1F; --ink2:#6E6E73; --ink3:#86868B; --surface:#F5F5F7; --line:#EBEBED;
    --green-ink:#1B873F; --green-soft:rgba(52,199,89,.14);
    --paper:#FFFDF8; --paper-edge:#EFE4D2;
    --mono:'SF Mono', ui-monospace, 'Menlo', monospace; --ease:cubic-bezier(0.16,1,0.3,1);
    color: var(--ink);
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif; }
  .cvrail .rk { font-size: 11px; font-weight: 600; letter-spacing: .09em; text-transform: uppercase;
    color: var(--ink3); display: block; margin: 18px 0 8px; }
  .cvrail .rk:first-child { margin-top: 0; }

  .cvrail .hero { background: #1D1D1F; color: #fff; border-radius: 14px; padding: 16px 17px 14px; }
  .cvrail .hero .n { font-size: 34px; font-weight: 700; letter-spacing: -.03em; color: var(--brand); line-height: 1; }
  .cvrail .hero .hk { font-family: var(--mono); font-size: 9px; font-weight: 700; letter-spacing: .16em;
    color: rgba(255,255,255,.5); margin-top: 5px; }
  .cvrail .hero .sub { display: flex; gap: 16px; margin-top: 12px; padding-top: 11px; border-top: 1px solid rgba(255,255,255,.12); }
  .cvrail .hero .sub > div { font-size: 13px; font-weight: 600; }
  .cvrail .hero .sub .kk { display: block; font-family: var(--mono); font-size: 8px; font-weight: 700;
    letter-spacing: .12em; color: rgba(255,255,255,.45); margin-top: 2px; }

  .cvrail .search { display: flex; align-items: center; gap: 8px; background: var(--surface);
    border-radius: 10px; padding: 9px 12px; margin: 14px 0 4px; }
  .cvrail .search svg { width: 13px; height: 13px; color: var(--ink3); flex: none; }
  .cvrail .search input { border: none; background: none; outline: none; font: inherit; font-size: 13.5px;
    color: var(--ink); width: 100%; }
  .cvrail .search input::placeholder { color: var(--ink3); }

  .cvrail .row { display: flex; align-items: center; gap: 10px; padding: 9px 6px; margin: 0 -6px;
    border-radius: 12px; cursor: pointer; width: calc(100% + 12px); text-align: left;
    background: none; border: none; font: inherit; color: inherit; transition: background .2s var(--ease); }
  .cvrail .row:hover { background: var(--surface); }
  .cvrail .ava { width: 36px; height: 36px; border-radius: 50%; background: #D2D2D7 center/cover no-repeat; flex: none; }
  .cvrail .who { min-width: 0; }
  .cvrail .nm { font-size: 13.5px; font-weight: 700; letter-spacing: -.01em; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; }
  .cvrail .mt { font-family: var(--mono); font-size: 8.5px; color: var(--ink3); margin-top: 3px; letter-spacing: .05em; }
  .cvrail .row svg { margin-left: auto; }
  .cvrail .chip { font-family: var(--mono); font-size: 10px; font-weight: 700; letter-spacing: .05em;
    padding: 4px 9px; border-radius: 980px; flex: none; }
  .cvrail .chip.track { background: var(--green-soft); color: var(--green-ink); }
  .cvrail .chip.fit { background: #1D1D1F; color: var(--brand); }
  .cvrail .chip.today { background: var(--brand-soft); color: var(--brand-ink); font-size: 8.5px; }
  .cvrail .chip.none { visibility: hidden; padding: 4px 0; }
  .cvrail .empty { font-size: 12.5px; color: var(--ink3); line-height: 1.55; padding: 6px 0; }
  .cvrail .adminlink { display: block; margin-top: 22px; font-family: var(--mono); font-size: 10px;
    font-weight: 700; letter-spacing: .14em; color: var(--ink3); text-decoration: none; text-transform: uppercase; }
  .cvrail .adminlink:hover { color: var(--ink); }

  .cvrail .how { font-size: 13px; color: var(--ink2); line-height: 1.55; }
  .cvrail .how .step { display: flex; gap: 10px; margin-bottom: 11px; }
  .cvrail .how .step i { font-style: normal; font-family: var(--mono); font-size: 10px; font-weight: 700;
    color: var(--brand-ink); padding-top: 2px; flex: none; }

  .cvrail .cv-save { background: var(--paper); border: 1px solid var(--paper-edge); border-radius: 14px;
    padding: 17px 16px; margin: 0; }
  .cvrail .cv-save-t { font-size: 16px; font-weight: 700; letter-spacing: -.01em; }
  .cvrail .cv-save-s { font-size: 12.5px; color: var(--ink2); line-height: 1.5; margin: 5px 0 11px; }
  .cvrail .cv-save-form { display: block; }
  .cvrail .cv-in { width: 100%; background: #fff; border: 1px solid var(--line); border-radius: 10px;
    font: inherit; font-size: 14px; padding: 11px 12px; color: var(--ink); outline: none; margin-bottom: 7px; }
  .cvrail .cv-in:focus { box-shadow: 0 0 0 3px rgba(0,0,0,.06); }
  .cvrail .cv-save-btn { width: 100%; background: #000; color: #fff; border: none; cursor: pointer;
    border-radius: 980px; padding: 12px; font: inherit; font-size: 14px; font-weight: 600; }
  .cvrail .cv-save-btn:disabled { opacity: .4; cursor: default; }
  .cvrail .cv-save-sent { font-size: 13.5px; color: var(--green-ink); font-weight: 500; }
  .cvrail .cv-save-err { font-size: 12px; color: #FF3B30; margin-top: 7px; }

  .cvrail .hero .flame { display: inline-block; }
  @keyframes cvrFlamePop {
    0% { transform: scale(1) rotate(0deg); }
    45% { transform: scale(1.28) rotate(-9deg); }
    75% { transform: scale(.94) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
  }
  .cvrail .hero .flame.pop { animation: cvrFlamePop .5s var(--ease); }
  @media (prefers-reduced-motion: reduce) {
    .cvrail .hero .flame.pop { animation: none !important; }
  }
`;

function StreakStat({ streak }) {
  const { display, flashing, direction } = useCountUp(streak, { duration: 600 });
  return (
    <div>
      <span className={`flame${flashing && direction === 'up' ? ' pop' : ''}`}>🔥</span>{' '}
      <span className={flashing ? `ft-tick ${direction}` : 'ft-tick'}>{display}</span>
      <span className="kk">STREAK</span>
    </div>
  );
}

function Row({ draw, todayLineId, onPick }) {
  const line = draw.line;
  const { meta, chip } = drawRowState(draw, todayLineId);
  return (
    <button type="button" className="row" onClick={() => onPick(draw)}>
      <span className="ava" style={line.player.sleeperId ? { backgroundImage: `url(${SLEEPERCDN}${line.player.sleeperId}.jpg)` } : undefined} />
      <span className="who">
        <span className="nm" style={{ display: 'block' }}>{line.player.name}</span>
        <span className="mt" style={{ display: 'block' }}>{meta}</span>
      </span>
      <CurveSpark points={draw.points} marks={line.marks} startValue={line.startValue}
        startDate={line.startDate} horizonWeeks={line.horizonWeeks} />
      {chip ? <span className={`chip ${chip.kind}`}>{chip.label}</span> : <span className="chip none">0</span>}
    </button>
  );
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
  </svg>
);

export default function CurveRail({ user, tape, todayLineId, onPick }) {
  const [q, setQ] = useState('');
  const draws = useMemo(() => (tape && Array.isArray(tape.draws) ? tape.draws : []), [tape]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return draws;
    return draws.filter((d) => (d.line.player.name || '').toLowerCase().includes(needle));
  }, [draws, q]);

  if (!tape) return null;

  // Never drawn — the one place "how it works" lives.
  if (!draws.length) {
    return (
      <div className="cvrail">
        <style>{styles}</style>
        <span className="rk">How it works</span>
        <div className="how">
          <div className="step"><i>01</i><div>One player a day. Draw where his KTC value goes.</div></div>
          <div className="step"><i>02</i><div>Lock it — then you see everyone else's lines.</div></div>
          <div className="step"><i>03</i><div>KTC updates every morning and grades you, day by day.</div></div>
        </div>
        <div className="empty">Your record starts with your first line.</div>
      </div>
    );
  }

  const open = filtered.filter((d) => d.line.status !== 'settled');
  const settled = filtered.filter((d) => d.line.status === 'settled');
  const beats = draws.filter((d) => d.line.status === 'settled' && d.beatBand).length;
  const openCount = draws.filter((d) => d.line.status !== 'settled').length;

  return (
    <div className="cvrail">
      <style>{styles}</style>

      {user ? (
        <div className="hero">
          <div className="n">{tape.accuracy != null ? Math.round(tape.accuracy) : '—'}</div>
          <div className="hk">{tape.gradedCount ? `ACCURACY · ${tape.gradedCount} GRADED` : 'ACCURACY · FIRST GRADE TONIGHT'}</div>
          <div className="sub">
            <StreakStat streak={tape.streak} />
            <div>{openCount}<span className="kk">OPEN</span></div>
            <div>{tape.settledCount ? `${beats} of ${tape.settledCount}` : '—'}<span className="kk">BEAT CONSENSUS</span></div>
          </div>
        </div>
      ) : (
        <SaveStreak streak={tape.streak} />
      )}

      {draws.length > 2 ? (
        <div className="search">
          <SearchIcon />
          <input placeholder="Find a player…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      ) : null}

      {open.length ? (
        <>
          <span className="rk">Open</span>
          {open.map((d) => <Row key={d.id} draw={d} todayLineId={todayLineId} onPick={onPick} />)}
        </>
      ) : null}
      {settled.length ? (
        <>
          <span className="rk">Settled</span>
          {settled.map((d) => <Row key={d.id} draw={d} todayLineId={todayLineId} onPick={onPick} />)}
        </>
      ) : null}
      {q && !open.length && !settled.length ? (
        <div className="empty">No line on him yet.</div>
      ) : null}

      {user && user.isAdmin ? (
        <a className="adminlink" href="/curve/schedule">Schedule →</a>
      ) : null}
    </div>
  );
}
